import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useCandidateDrawer } from "@/components/CandidateDrawer";
import { dbsPillClass, fmtDate, daysUntil, initials } from "@/lib/cc";
import { ShieldCheck, AlertTriangle } from "lucide-react";

export default function DbsPage() {
  const [allCands, setAllCands] = useState([]);
  const { open } = useCandidateDrawer();

  useEffect(() => {
    api.get("/candidates").then(({ data }) => setAllCands(data));
  }, []);

  // Per user requirement (Feb 2026): DBS tracking only applies to candidates
  // who have passed the phone screen — i.e., progressed to Interview, Offer,
  // Onboarding or Hired. Earlier-stage and rejected candidates stay out.
  const POST_PHONE_STAGES = ["interview", "offer", "onboarding", "hired"];
  const cands = allCands.filter((c) => POST_PHONE_STAGES.includes(c.stage));

  const counts = cands.reduce((acc, c) => {
    const s = c.dbs?.status || "not_started";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const expiring = cands.filter((c) => c.dbs?.expiry_date && daysUntil(c.dbs.expiry_date) <= 90 && daysUntil(c.dbs.expiry_date) > 0);

  const groups = [
    { key: "not_started", label: "Not Started" },
    { key: "sent", label: "Sent" },
    { key: "in_progress", label: "In Progress" },
    { key: "received", label: "Received" },
    { key: "clear", label: "Clear" },
    { key: "not_clear", label: "Not Clear" },
  ];

  return (
    <div className="fade-up">
      <div className="mb-6">
        <div className="label-eyebrow mb-2">Compliance</div>
        <h1 className="font-display text-4xl font-bold flex items-center gap-3"><ShieldCheck className="w-8 h-8" />DBS Tracking</h1>
        <p className="text-[var(--cc-text-soft)] mt-1">Enhanced DBS checks for candidates who have passed the phone screen · manually entered by HR Admin / Recruiter.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        {groups.map((g) => (
          <div key={g.key} className="cc-card p-4">
            <div className="label-eyebrow mb-2">{g.label}</div>
            <div className="font-dash text-3xl font-bold">{counts[g.key] || 0}</div>
          </div>
        ))}
      </div>

      {expiring.length > 0 && (
        <div className="cc-card p-5 mb-6 border-l-4" style={{ borderLeftColor: "var(--cc-accent)" }}>
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-[var(--cc-accent)]" />
            <span className="font-semibold">Expiring within 90 days · {expiring.length}</span>
          </div>
          <div className="space-y-2">
            {expiring.map((c) => (
              <button key={c.id} onClick={() => open(c.id)} className="flex items-center justify-between w-full p-2 rounded hover:bg-[var(--cc-surface)] text-left">
                <span className="text-sm font-medium">{c.first_name} {c.last_name}</span>
                <span className="text-xs text-[var(--cc-accent)]">{daysUntil(c.dbs.expiry_date)} days left · {fmtDate(c.dbs.expiry_date)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="cc-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[var(--cc-surface)]">
            <tr className="text-left">
              <th className="px-5 py-3 label-eyebrow">Candidate</th>
              <th className="px-5 py-3 label-eyebrow">Status</th>
              <th className="px-5 py-3 label-eyebrow">Reference</th>
              <th className="px-5 py-3 label-eyebrow">Issued</th>
              <th className="px-5 py-3 label-eyebrow">Expiry</th>
              <th className="px-5 py-3 label-eyebrow">Update Svc</th>
            </tr>
          </thead>
          <tbody>
            {cands.map((c) => (
              <tr key={c.id} className="border-t border-[var(--cc-border)] hover:bg-[var(--cc-surface)] cursor-pointer" onClick={() => open(c.id)} data-testid={`dbs-row-${c.id}`}>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                         style={{ background: "var(--cc-primary)" }}>{initials(c.first_name, c.last_name)}</div>
                    <div>
                      <div className="font-semibold">{c.first_name} {c.last_name}</div>
                      <div className="text-xs text-[var(--cc-text-soft)]">{c.job_title}</div>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3"><span className={`pill ${dbsPillClass(c.dbs?.status)}`}>{(c.dbs?.status || "not started").replace("_", " ")}</span></td>
                <td className="px-5 py-3 text-[var(--cc-text-soft)]">{c.dbs?.reference || "—"}</td>
                <td className="px-5 py-3">{fmtDate(c.dbs?.issued_date)}</td>
                <td className="px-5 py-3">{fmtDate(c.dbs?.expiry_date)}</td>
                <td className="px-5 py-3">{c.dbs?.on_update_service ? "Yes" : "No"}</td>
              </tr>
            ))}
            {cands.length === 0 && (
              <tr><td colSpan="6" className="text-center py-10 text-[var(--cc-text-soft)]" data-testid="dbs-empty">
                No candidates have passed the phone screen yet.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
